/**
 * @file _StepperComponent.ts
 * @module _admin/assets/ts/components/_StepperComponent
 * @description Implements the  StepperComponent interactive admin-shell component, including initialization, state management, and DOM event handling.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 1.3.0
 */

import {
  getElementIndex,
  getUniqueIdWithPrefix,
  ElementAnimateUtil,
  EventHandlerUtil,
  DataUtil,
  DOMEventHandlerUtil,
  ElementStyleUtil,
} from "../_utils/index";

export interface IStepperOptions {
  startIndex: number;
  animation: boolean;
  animationSpeed: string;
  animationNextClass: string;
  animationPreviousClass: string;
}

const defaultStepperOptions: IStepperOptions = {
  startIndex: 1,
  animation: false,
  animationSpeed: "0.3s",
  animationNextClass: "animate__animated animate__slideInRight animate__fast",
  animationPreviousClass:
    "animate__animated animate__slideInLeft animate__fast",
};

/**
 * @class StepperComponent
 * @description UI component for multi-step wizards in the admin panel. Manages navigation states, UI updates, step transitions, and event triggers.
 */
class StepperComponent {
  element: HTMLElement;
  options: IStepperOptions;
  instanceUid: string;
  steps: NodeListOf<HTMLElement>;
  btnNext: HTMLElement | null;
  btnPrev: HTMLElement | null;
  btnSubmit: HTMLElement | null;
  totatStepsNumber: number = 0;
  passedStepIndex: number = 0;
  currentStepIndex: number = 1;

  /**
   * Creates an instance of StepperComponent.
   *
   * @param {HTMLElement} _element - Container element for the stepper.
   * @param {IStepperOptions} options - Configuration options for the stepper.
   */
  constructor(_element: HTMLElement, options: IStepperOptions) {
    this.element = _element;
    this.options = Object.assign(defaultStepperOptions, options);
    this.instanceUid = getUniqueIdWithPrefix("stepper");

    // Elements
    this.steps = this.element.querySelectorAll(
      '[data-kt-stepper-element="nav"]'
    );
    this.btnNext = this.element.querySelector(
      '[data-kt-stepper-action="next"]'
    );
    this.btnPrev = this.element.querySelector(
      '[data-kt-stepper-action="previous"]'
    );
    this.btnSubmit = this.element.querySelector(
      '[data-kt-stepper-action="submit"]'
    );

    // Variables
    this.totatStepsNumber = this.steps?.length | 0;
    this.passedStepIndex = 0;
    this.currentStepIndex = 1;

    // Set Current Step
    if (this.options.startIndex > 1) {
      this._goTo(this.options.startIndex);
    }

    // Event Handlers
    this.initHandlers();

    // Bind Instance
    DataUtil.set(this.element, "stepper", this);
  }

  /**
   * Navigates to a specific step index in the stepper component.
   *
   * @param {number} index - Target step index to navigate to.
   */
  private _goTo = (index: number) => {
    EventHandlerUtil.trigger(this.element, "kt.stepper.change");
    // Skip if this step is already shown
    if (
      index === this.currentStepIndex ||
      index > this.totatStepsNumber ||
      index < 0
    ) {
      return;
    }

    // Validate step number
    index = parseInt(index.toString());
    // Set current step
    this.passedStepIndex = this.currentStepIndex;
    this.currentStepIndex = index;

    // Refresh elements
    this.refreshUI();

    EventHandlerUtil.trigger(this.element, "kt.stepper.changed");
  };

  /**
   * Initializes event handlers for navigation buttons and step action triggers.
   */
  private initHandlers = () => {
    this.btnNext?.addEventListener("click", (e: Event) => {
      e.preventDefault();

      EventHandlerUtil.trigger(this.element, "kt.stepper.next", e);
    });

    this.btnPrev?.addEventListener("click", (e: Event) => {
      e.preventDefault();

      EventHandlerUtil.trigger(this.element, "kt.stepper.previous", e);
    });

    DOMEventHandlerUtil.on(
      this.element,
      '[data-kt-stepper-action="step"]',
      "click",
      (e: Event) => {
        e.preventDefault();

        if (this.steps && this.steps.length > 0) {
          for (let i = 0; i < this.steps.length; i++) {
            if ((this.steps[i] as HTMLElement) === this.element) {
              let index = i + 1;

              const stepDirection = this._getStepDirection(index);
              EventHandlerUtil.trigger(
                this.element,
                `stepper.${stepDirection}`,
                e
              );
              return;
            }
          }
        }
      }
    );
  };

  /**
   * Determines whether moving to a target step index is forward or backward.
   *
   * @param {number} index - Target step index.
   * @returns {string} Direction string, either "next" or "previous".
   */
  private _getStepDirection = (index: number) => {
    return index > this.currentStepIndex ? "next" : "previous";
  };

  /**
   * Retrieves the content DOM element for a given step index.
   *
   * @param {number} index - Step index.
   * @returns {HTMLElement|boolean} The content element if found, otherwise false.
   */
  private getStepContent = (index: number) => {
    const content = this.element.querySelectorAll(
      '[data-kt-stepper-element="content"]'
    );
    if (!content) {
      return false;
    }

    if (content[index - 1]) {
      return content[index - 1];
    }

    return false;
  };

  /**
   * Gets the index of the last step.
   *
   * @returns {number} The total steps number.
   */
  private getLastStepIndex = () => {
    return this.totatStepsNumber;
  };

  /**
   * Gets the total number of steps in the stepper.
   *
   * @returns {number} Total steps count.
   */
  private getTotalStepsNumber = () => {
    return this.totatStepsNumber;
  };

  /**
   * Refreshes the UI elements, classes, and animation states of the stepper based on the current step index.
   */
  private refreshUI = () => {
    let state = "";

    if (this.isLastStep()) {
      state = "last";
    } else if (this.isFirstStep()) {
      state = "first";
    } else {
      state = "between";
    }

    // Set state class
    this.element.classList.remove("last");
    this.element.classList.remove("first");
    this.element.classList.remove("between");

    this.element.classList.add(state);

    // Step Items
    const elements = this.element.querySelectorAll(
      '[data-kt-stepper-element="nav"], [data-kt-stepper-element="content"], [data-kt-stepper-element="info"]'
    );

    if (!elements || elements.length <= 0) {
      return;
    }

    for (var i = 0, len = elements.length; i < len; i++) {
      var element = elements[i] as HTMLElement;
      var index = getElementIndex(element) + 1;

      element.classList.remove("current");
      element.classList.remove("completed");
      element.classList.remove("pending");

      if (index === this.currentStepIndex) {
        element.classList.add("current");

        if (
          this.options.animation !== false &&
          element.getAttribute("data-kt-stepper-element") === "content"
        ) {
          ElementStyleUtil.set(
            element,
            "animationDuration",
            this.options.animationSpeed
          );

          const animation =
            this._getStepDirection(this.passedStepIndex) === "previous"
              ? this.options.animationPreviousClass
              : this.options.animationNextClass;
          ElementAnimateUtil.animateClass(element, animation);
        }
      } else {
        if (index < this.currentStepIndex) {
          element.classList.add("completed");
        } else {
          element.classList.add("pending");
        }
      }
    }
  };

  /**
   * Checks if the stepper is currently on the last step.
   *
   * @returns {boolean} True if on the last step, false otherwise.
   */
  private isLastStep = () => {
    return this.currentStepIndex === this.totatStepsNumber;
  };

  /**
   * Checks if the stepper is currently on the first step.
   *
   * @returns {boolean} True if on the first step, false otherwise.
   */
  private isFirstStep = () => {
    return this.currentStepIndex === 1;
  };

  /**
   * Checks if the current step is between the first and last steps.
   *
   * @returns {boolean} True if between steps, false otherwise.
   */
  private isBetweenStep = () => {
    return this.isLastStep() === false && this.isFirstStep() === false;
  };

  //   ///////////////////////
  //   // ** Public API  ** //
  //   ///////////////////////

  //   // Plugin API
  /**
   * Navigates to a specific step index.
   *
   * @param {number} index - Target step index.
   */
  public goto = (index: number) => {
    return this._goTo(index);
  };

  /**
   * Advances to the next step.
   */
  public goNext = () => {
    return this.goto(this.getNextStepIndex());
  };

  /**
   * Moves back to the previous step.
   */
  public goPrev = () => {
    return this.goto(this.getPrevStepIndex());
  };

  /**
   * Jumps to the first step.
   */
  public goFirst = () => {
    return this.goto(1);
  };

  /**
   * Jumps to the last step.
   */
  public goLast = () => {
    return this.goto(this.getLastStepIndex());
  };

  /**
   * Gets the current step index.
   *
   * @returns {number} Current step index.
   */
  public getCurrentStepIndex = () => {
    return this.currentStepIndex;
  };

  /**
   * Calculates the next step index.
   *
   * @returns {number} Next step index.
   */
  public getNextStepIndex = () => {
    if (this.totatStepsNumber >= this.currentStepIndex + 1) {
      return this.currentStepIndex + 1;
    } else {
      return this.totatStepsNumber;
    }
  };

  /**
   * Gets the passed (previous active) step index.
   *
   * @returns {number} Passed step index.
   */
  public getPassedStepIndex = () => {
    return this.passedStepIndex;
  };

  /**
   * Calculates the previous step index.
   *
   * @returns {number} Previous step index.
   */
  public getPrevStepIndex = () => {
    if (this.currentStepIndex - 1 > 1) {
      return this.currentStepIndex - 1;
    } else {
      return 1;
    }
  };

  /**
   * Returns the root element of the stepper.
   *
   * @param {number} index - Step index (unused).
   * @returns {HTMLElement} The root element.
   */
  public getElement = (index: number) => {
    return this.element;
  };

  // Event API
  /**
   * Registers an event handler on the stepper element.
   *
   * @param {string} name - Event name.
   * @param {Function} handler - Event handler callback.
   * @returns {object} EventHandlerUtil result.
   */
  public on = (name: string, handler: Function) => {
    return EventHandlerUtil.on(this.element, name, handler);
  };

  /**
   * Registers a one-time event handler on the stepper element.
   *
   * @param {string} name - Event name.
   * @param {Function} handler - Event handler callback.
   * @returns {object} EventHandlerUtil result.
   */
  public one = (name: string, handler: Function) => {
    return EventHandlerUtil.one(this.element, name, handler);
  };

  /**
   * Removes an event handler from the stepper element.
   *
   * @param {string} name - Event name.
   * @param {string} handlerId - Handler identifier.
   * @returns {object} EventHandlerUtil result.
   */
  public off = (name: string, handlerId: string) => {
    return EventHandlerUtil.off(this.element, name, handlerId);
  };

  /**
   * Destroys the stepper instance.
   */
  public destroy = () => {
    console.log("destroy stepper");
  };

  /**
   * Triggers a custom event on the stepper element.
   *
   * @param {string} name - Event name.
   * @param {Event} event - Event object.
   * @returns {object} EventHandlerUtil result.
   */
  public trigger = (name: string, event: Event) => {
    return EventHandlerUtil.trigger(this.element, name, event);
  };

  // Static methods
  /**
   * Checks if an HTML element has an attached stepper instance.
   *
   * @static
   * @param {HTMLElement} element - Target element.
   * @returns {boolean} True if an instance exists, false otherwise.
   */
  public static hasInstace(element: HTMLElement): boolean {
    return DataUtil.has(element, "stepper");
  }

  /**
   * Retrieves the stepper instance associated with an element.
   *
   * @static
   * @param {HTMLElement} element - Target element.
   * @returns {StepperComponent|undefined} The stepper component instance or undefined.
   */
  public static getInstance(
    element: HTMLElement
  ): StepperComponent | undefined {
    if (element !== null && StepperComponent.hasInstace(element)) {
      const data = DataUtil.get(element, "stepper");
      if (data) {
        return data as StepperComponent;
      }
    }
  }

  // Create Instances
  /**
   * Creates stepper instances for all elements matching a CSS selector.
   *
   * @static
   * @param {string} selector - CSS selector query.
   */
  public static createInstances(selector: string): void {
    const elements = document.body.querySelectorAll(selector);
    elements.forEach((element) => {
      const item = element as HTMLElement;
      let stepper = StepperComponent.getInstance(item);
      if (!stepper) {
        stepper = new StepperComponent(item, defaultStepperOptions);
      }
    });
  }

  /**
   * Creates or retrieves a stepper instance for an element.
   *
   * @param {HTMLElement} element - Target element.
   * @param {IStepperOptions} [options=defaultStepperOptions] - Configuration options.
   * @returns {StepperComponent|null} Stepper component instance or null.
   */
  public static createInsance = (
    element: HTMLElement,
    options: IStepperOptions = defaultStepperOptions
  ): StepperComponent | null => {
    if (!element) {
      return null;
    }
    let stepper = StepperComponent.getInstance(element);
    if (!stepper) {
      stepper = new StepperComponent(element, options);
    }
    return stepper;
  };

  /**
   * Bootstraps stepper components across the document using a selector attribute.
   *
   * @static
   * @param {string} [attr="[data-kt-stepper]"] - Target attribute selector.
   */
  public static bootstrap(attr: string = "[data-kt-stepper]") {
    StepperComponent.createInstances(attr);
  }
}

export { StepperComponent, defaultStepperOptions };
